import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const DialogContext = createContext(null);

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'var(--bg-modal-overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000
};

const modalStyle = {
  width: '460px',
  maxWidth: '92vw',
  background: 'var(--bg-modal)',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  boxShadow: '0 12px 30px var(--shadow-strong)',
  color: 'var(--text-primary)',
  padding: '18px'
};

const titleStyle = {
  margin: 0,
  fontSize: '16px',
  fontWeight: 700
};

const messageStyle = {
  marginTop: '10px',
  fontSize: '13px',
  lineHeight: 1.5,
  color: 'var(--text-secondary)',
  whiteSpace: 'pre-wrap',
  maxHeight: '48vh',
  overflowY: 'auto'
};

const inputStyle = {
  marginTop: '12px',
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  padding: '9px 10px'
};

const actionsStyle = {
  marginTop: '16px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px'
};

const secondaryButtonStyle = {
  padding: '7px 12px',
  borderRadius: '6px',
  border: '1px solid var(--border-color)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '12px'
};

const primaryButtonStyle = {
  padding: '7px 12px',
  borderRadius: '6px',
  border: '1px solid var(--accent-color)',
  background: 'var(--accent-color)',
  color: 'var(--text-inverse)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '12px'
};

export function DialogProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [activeDialog, setActiveDialog] = useState(null);
  const [promptValue, setPromptValue] = useState('');

  useEffect(() => {
    if (activeDialog || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setActiveDialog(next);
    setPromptValue(next.defaultValue || '');
  }, [queue, activeDialog]);

  const enqueueDialog = useCallback((dialog) => (
    new Promise((resolve) => {
      setQueue((prev) => [...prev, { ...dialog, resolve }]);
    })
  ), []);

  const showAlert = useCallback((message, options = {}) => (
    enqueueDialog({
      type: 'alert',
      title: options.title || 'Notice',
      message: String(message ?? '')
    })
  ), [enqueueDialog]);

  const showConfirm = useCallback((message, options = {}) => (
    enqueueDialog({
      type: 'confirm',
      title: options.title || 'Confirm',
      message: String(message ?? ''),
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel'
    })
  ), [enqueueDialog]);

  const showPrompt = useCallback((message, options = {}) => (
    enqueueDialog({
      type: 'prompt',
      title: options.title || 'Input Required',
      message: String(message ?? ''),
      confirmText: options.confirmText || 'Submit',
      cancelText: options.cancelText || 'Cancel',
      defaultValue: options.defaultValue || '',
      placeholder: options.placeholder || ''
    })
  ), [enqueueDialog]);

  const closeDialog = useCallback((value) => {
    if (activeDialog?.resolve) {
      activeDialog.resolve(value);
    }
    setActiveDialog(null);
    setPromptValue('');
  }, [activeDialog]);

  const contextValue = useMemo(() => ({
    showAlert,
    showConfirm,
    showPrompt
  }), [showAlert, showConfirm, showPrompt]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const originalAlert = window.alert;
    const originalConfirm = window.confirm;
    const originalPrompt = window.prompt;
    window.alert = (message) => showAlert(message);
    window.confirm = (message) => showConfirm(message);
    window.prompt = (message, defaultValue = '') => showPrompt(message, { defaultValue });
    return () => {
      window.alert = originalAlert;
      window.confirm = originalConfirm;
      window.prompt = originalPrompt;
    };
  }, [showAlert, showConfirm, showPrompt]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {activeDialog && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={titleStyle}>{activeDialog.title}</h3>
            <div style={messageStyle}>{activeDialog.message}</div>
            {activeDialog.type === 'prompt' && (
              <input
                autoFocus
                style={inputStyle}
                value={promptValue}
                placeholder={activeDialog.placeholder}
                onChange={(e) => setPromptValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') closeDialog(promptValue);
                }}
              />
            )}
            <div style={actionsStyle}>
              {activeDialog.type !== 'alert' && (
                <button style={secondaryButtonStyle} onClick={() => closeDialog(activeDialog.type === 'prompt' ? null : false)}>
                  {activeDialog.cancelText || 'Cancel'}
                </button>
              )}
              <button
                style={primaryButtonStyle}
                onClick={() => {
                  if (activeDialog.type === 'confirm') closeDialog(true);
                  else if (activeDialog.type === 'prompt') closeDialog(promptValue);
                  else closeDialog(true);
                }}
              >
                {activeDialog.confirmText || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}
